import { useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert } from "@/components/ui/alert";
import { trpc } from "../../trpc/index";

type FileFormat = "excel" | "csv";
type DownloadStatus = "idle" | "downloading" | "completed";

type Props = {
  merchantId: string;
};

const BillableReportCard = ({ merchantId }: Props) => {
  const currentDate = new Date();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

  const [startDate, setStartDate] = useState(firstDayOfMonth.toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(currentDate.toISOString().split("T")[0]);
  const [format, setFormat] = useState("Excel (.xlsx)");
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>("idle");

  const { mutate: downloadReport, isLoading: isDownloading } =
    trpc.billableEvent.downloadReport.useMutation();

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);

    if (newStartDate > endDate) {
      setEndDate(newStartDate);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);

    if (newEndDate < startDate) {
      setStartDate(newEndDate);
    }
  };

  const getFileFormat = (): FileFormat => {
    return format.includes("Excel") ? "excel" : "csv";
  };

  const getFormatExtension = () => {
    if (format.includes("Excel")) return "XLSX";
    if (format.includes("CSV")) return "CSV";
    return "XLSX";
  };

  const handleGenerateReport = () => {
    if (!merchantId) return;

    setDownloadStatus("downloading");

    downloadReport(
      {
        merchantId: merchantId,
        createdAt: {
          start: startDate,
          end: endDate,
        },
        fileFormat: getFileFormat(),
      },
      {
        onSuccess: (data) => {
          setDownloadStatus("completed");

          if (data.url) {
            const link = document.createElement("a");
            link.href = data.url;
            link.setAttribute(
              "download",
              `Reporte_Conversaciones_Facturables.${getFormatExtension().toLowerCase()}`
            );
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
        onError: (error) => {
          console.error("Failed to download report:", error);
          setDownloadStatus("idle");
        },
      }
    );
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-4">
        <div>
          <p className="mb-2 text-lg font-semibold text-gray-800">
            Descargar Reporte de Conversaciones Facturables
          </p>
          <p className="text-sm text-gray-600">
            Genere un reporte detallado de las conversaciones facturables agrupadas por prospecto.
          </p>
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="flex-1">
            <p className="mb-1 text-xs text-gray-secondary">Fecha de inicio</p>
            <Input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              max={endDate}
              min="2020-01-01"
              className="bg-white"
            />
          </div>

          <div className="flex-1">
            <p className="mb-1 text-xs text-gray-secondary">Fecha de fin</p>
            <Input
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              max={currentDate.toISOString().split("T")[0]}
              min={startDate}
              className="bg-white"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="flex-1">
            <p className="mb-1 text-xs text-gray-secondary">Formato</p>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="Excel (.xlsx)">Excel (.xlsx)</option>
              <option value="CSV (.csv)">CSV (.csv)</option>
            </select>
          </div>

          <div className="flex flex-1 items-end">
            <Button
              onClick={handleGenerateReport}
              disabled={downloadStatus === "downloading" || isDownloading}
              className="w-full"
            >
              <Download className="size-4" />
              Generar y Descargar
            </Button>
          </div>
        </div>

        {/* Download Status Notifications */}
        {downloadStatus === "downloading" && (
          <Alert status="info">
            <p>Generando reporte en formato {getFormatExtension()}...</p>
          </Alert>
        )}
        {downloadStatus === "completed" && (
          <Alert status="success">
            <p>
              El reporte ({getFormatExtension()}) se ha generado y la descarga debería comenzar.
            </p>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default BillableReportCard;
